import { type ColumnType, Table, TableRowMenuCell } from '@/components';
import { useMasterTablePermission } from '@/features/authentication/_hooks/permission';
import { fillZeroToProjectCode } from '@/features/project/_utils/code-formatter';
import {
  TABLE_DATETIME_WIDTH,
  TABLE_ROW_MENU_WIDTH,
  datetimeFormat,
} from '@/utils';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useDrawerState } from '../../components';
import { DEFAULT_PAGINATION_SETTING } from '../../utils/table';
import { useReservedProjects } from '../_hooks/reserved-project';
import type { ReservedProjectModel } from '../_models/reserved-project';
import { ReservedProjectDeleteModal } from './ReservedProjectDeleteModal';

export const ReservedProjectTable: FC = () => {
  const { t } = useTranslation();
  const { setDrawerState } = useDrawerState();

  const [selectedReservedProjectId, setSelectedReservedProjectId] = useState<
    string | null
  >(null);

  const editPermission = useMasterTablePermission(true);

  const { data, isLoading } = useReservedProjects();

  return (
    <>
      <Table
        rowKey="id"
        dataSource={data}
        loading={isLoading}
        pagination={DEFAULT_PAGINATION_SETTING}
        columns={[
          {
            title: t('reserved-project-code'),
            dataIndex: 'code',
            width: 100,
            render: fillZeroToProjectCode,
          },
          {
            title: t('reserved-project-name'),
            dataIndex: 'name',
          },
          {
            title: t('created-datetime'),
            dataIndex: 'createdAt',
            width: TABLE_DATETIME_WIDTH,
            render: (datetime) => datetimeFormat({ datetime }),
          },
          {
            title: t('updated-datetime'),
            dataIndex: 'updatedAt',
            width: TABLE_DATETIME_WIDTH,
            render: (datetime) => datetimeFormat({ datetime }),
          },
          ...(editPermission.data
            ? [
                {
                  title: '',
                  key: 'actions',
                  fixed: 'right',
                  width: TABLE_ROW_MENU_WIDTH,
                  onCell: () => ({
                    style: { minWidth: TABLE_ROW_MENU_WIDTH },
                  }),
                  render: (_, record) => {
                    return (
                      <TableRowMenuCell
                        onEdit={() => {
                          setDrawerState({
                            open: true,
                            type: 'reserved-project',
                            isEditing: true,
                            id: record.id,
                          });
                        }}
                        items={[
                          {
                            key: 'delete',
                            label: t('delete'),
                            danger: true,
                            onClick: () => {
                              setSelectedReservedProjectId(record.id);
                            },
                          },
                        ]}
                      />
                    );
                  },
                } satisfies ColumnType<z.infer<typeof ReservedProjectModel>>,
              ]
            : []),
        ]}
      />
      <ReservedProjectDeleteModal
        selectedReservedProjectId={selectedReservedProjectId}
        onClose={() => {
          setSelectedReservedProjectId(null);
        }}
      />
    </>
  );
};
